import React from 'react';
import { Badge } from '@/modules/Badge';
import {
  BADGE_POSITION_STYLE,
  BADGE_POSITION_CLASSES,
} from '@/modules/InnerButton/styles/buttonVariants';
import { BadgeColor, BadgeTheme, BadgeSize } from '@/shared/types/badge';
import { IconFontColor } from '@/shared/types/types';
import type { LegacyIconName } from '@/shared/types/legacyIcon';
import { LegacyIcon } from '@/modules/LegacyIcon';
import type { IconProps } from '@/shared/types/icon';

const iconSizeByBadge: Record<'sm' | 'md' | 'lg' | 'dot', IconProps['size']> = {
  sm: 'xs',
  md: 'xs',
  lg: 'sm',
  dot: 'xs',
};

export interface LegacyButtonBadgeProps {
  shouldShow: boolean;
  badgeColor?: BadgeColor;
  badgeLabel?: string;
  badgeTheme?: BadgeTheme;
  badgeSize?: BadgeSize;
  badgeStroke?: boolean;
  badgeIcon?: LegacyIconName;
  badgeIconDuotonePrimary?: IconFontColor;
  badgeIconDuotoneSecondary?: IconFontColor;
  badgeIconDuotoneOpacityPrimary?: number;
  badgeIconDuotoneOpacitySecondary?: number;
  badgeLoading?: boolean;
  isAbsolute?: boolean;
}

export const LegacyButtonBadge: React.FC<LegacyButtonBadgeProps> = ({
  shouldShow,
  badgeColor,
  badgeLabel,
  badgeTheme = 'solid',
  badgeSize = 'sm',
  badgeStroke,
  badgeIcon,
  badgeIconDuotonePrimary,
  badgeIconDuotoneSecondary,
  badgeIconDuotoneOpacityPrimary,
  badgeIconDuotoneOpacitySecondary,
  badgeLoading,
  isAbsolute = false,
}) => {
  if (!shouldShow) {
    return null;
  }

  const effectiveSize = badgeSize || 'sm';
  const iconSlot =
    badgeIcon !== undefined && badgeIcon !== null && badgeSize !== 'dot' ? (
      <LegacyIcon
        name={badgeIcon}
        size={iconSizeByBadge[effectiveSize === 'dot' ? 'sm' : effectiveSize] ?? 'xs'}
        color="current"
        colorDuotonePrimary={badgeIconDuotonePrimary}
        colorDuotoneSecondary={badgeIconDuotoneSecondary}
        opacityDuotonePrimary={badgeIconDuotoneOpacityPrimary}
        opacityDuotoneSecondary={badgeIconDuotoneOpacitySecondary}
        aria-hidden
      />
    ) : undefined;

  const badge = (
    <Badge
      color={badgeColor || 'accent'}
      label={badgeLabel}
      theme={badgeTheme}
      size={badgeSize}
      stroke={badgeStroke}
      icon={undefined}
      iconSlot={iconSlot}
      iconDuotonePrimary={badgeIconDuotonePrimary}
      iconDuotoneSecondary={badgeIconDuotoneSecondary}
      iconDuotoneOpacityPrimary={badgeIconDuotoneOpacityPrimary}
      iconDuotoneOpacitySecondary={badgeIconDuotoneOpacitySecondary}
      loading={badgeLoading}
    />
  );

  if (isAbsolute) {
    return (
      <div className={BADGE_POSITION_CLASSES} style={BADGE_POSITION_STYLE}>
        {badge}
      </div>
    );
  }

  return <span className="ui-ml-4 ui-shrink-0">{badge}</span>;
};

LegacyButtonBadge.displayName = 'LegacyButtonBadge';
